import { useContext, useEffect, useRef } from "react";
import { NotificationContext } from "../../contexts/NotificationContext";
import { CheckoutContext } from '../../contexts/CheckoutContext'
import useDevice from '../../hooks/useDevice'
import { getRandomNumber } from '../../utils/number'
import {
  CheckoutComponentNotificationAttibutesType,
  CheckoutComponentNotificationType,
} from "../../types/builder";

const mapTexts: Record<CheckoutComponentNotificationType, (min: number, max: number) => string> = {
  [CheckoutComponentNotificationType.INTERESTED_LAST_24_HOURS]: (min: number, max: number) => {
    const value = getRandomNumber(min, max);
    return `${value} pessoa${value > 1 ? 's' : ''} interessadas nesse produto nas últimas 24 horas`;
  },
  [CheckoutComponentNotificationType.INTERESTED_LAST_WEEK]: (min: number, max: number) => {
    const value = getRandomNumber(min, max);
    return `${value} pessoa${value > 1 ? 's' : ''} interessadas nesse produto na última semana`;
  },
  [CheckoutComponentNotificationType.INTERESTED_RIGHT_NOW]: (min: number, max: number) => {
    const value = getRandomNumber(min, max);
    return `${value} pessoa${value > 1 ? 's' : ''} interessadas nesse produto agora`;
  },
  [CheckoutComponentNotificationType.PURCHASED_LAST_24_HOURS]: (min: number, max: number) => {
    const value = getRandomNumber(min, max);
    return `${value} compra${value > 1 ? 's' : ''} desse produto nas últimas 24 horas`;
  },
  [CheckoutComponentNotificationType.PURCHASED_LAST_WEEK]: (min: number, max: number) => {
    const value = getRandomNumber(min, max);
    return `${value} compra${value > 1 ? 's' : ''} desse produto na última semana`;
  },
};

/**
 * Apenas dispara as notificações no contexto quando a oferta/config está disponível.
 * A renderização sequencial no checkout é feita por NotificationToasts (em App).
 */
const CheckoutComponentNotification: React.FC<
  CheckoutComponentNotificationAttibutesType
> = () => {
  const { notify, clear } = useContext(NotificationContext);
  const { offer } = useContext(CheckoutContext);
  const { device } = useDevice();
  const lastOfferIdRef = useRef<string | null>(null);

  useEffect(() => {
    const offerId = offer?.id ?? null;
    const config = offer?.checkout?.config?.[device]?.extra?.notification?.attributes;
    if (!config?.enabled || !offerId) return;

    // Só repopula quando a oferta muda (evita duplicar ao re-renderizar)
    if (lastOfferIdRef.current === offerId) return;
    lastOfferIdRef.current = offerId;
    clear();

    const entries = (Object.entries(config) as [string, unknown][])
      .filter(([key]) => key !== 'enabled')
      .filter(([, n]) => (n as { enabled?: boolean })?.enabled);

    entries.forEach(([type, notification]) => {
      const attrs = notification as { min?: number; max?: number; exibitionTime?: number };
      const min = Number(attrs?.min ?? 0);
      const max = Number(attrs?.max ?? 0);
      const exibitionTime = Number(attrs?.exibitionTime ?? 5);
      const message = mapTexts[type as CheckoutComponentNotificationType]?.(min, max) ?? '';
      notify(message, type as CheckoutComponentNotificationType, exibitionTime);
    });
  }, [offer?.id, offer?.checkout?.config, device, notify, clear]);

  return null;
};

export default CheckoutComponentNotification;
